/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import {
  createProductGroupingPayload,
  productGroupingListResponse,
  updateProductGroupingPayload,
} from 'interfaces/ProductGrouping';
import { errorRedux } from 'interfaces/redux';
import {
  createProductGrouping,
  deleteProductGrouping,
  getProductGroupingById,
  getProductGroupingList,
  updateProductGrouping,
} from './service';

// Action: Listar agrupamentos
export const productGroupingList = createAsyncThunk<
  productGroupingListResponse,
  undefined,
  { rejectValue: errorRedux }
>('productGrouping/list', async (_, { rejectWithValue }) => {
  try {
    const data = await getProductGroupingList();
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Buscar agrupamento por ID
export const productGroupingById = createAsyncThunk<
  productGroupingListResponse,
  number,
  { rejectValue: errorRedux }
>('productGrouping/getById', async (id, { rejectWithValue }) => {
  try {
    const data = await getProductGroupingById(id);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Criar agrupamento
export const productGroupingCreate = createAsyncThunk<
  productGroupingListResponse,
  createProductGroupingPayload,
  { rejectValue: errorRedux }
>('productGrouping/create', async (payload, { rejectWithValue }) => {
  try {
    const data = await createProductGrouping(payload);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Atualizar agrupamento
export const productGroupingUpdate = createAsyncThunk<
  productGroupingListResponse,
  updateProductGroupingPayload,
  { rejectValue: errorRedux }
>('productGrouping/update', async (payload, { rejectWithValue }) => {
  try {
    const data = await updateProductGrouping(payload);
    return data;
  } catch (error) {
    const err = error as AxiosError<errorRedux>;
    return rejectWithValue(err.response?.data as errorRedux);
  }
});

// Action: Deletar agrupamento
export const productGroupingDelete = createAsyncThunk<void, number, { rejectValue: errorRedux }>(
  'productGrouping/delete',
  async (id, { rejectWithValue }) => {
    try {
      await deleteProductGrouping(id);
      return undefined;
    } catch (error) {
      const err = error as AxiosError<errorRedux>;
      return rejectWithValue(err.response?.data as errorRedux);
    }
  },
);

// Reducer
interface IProductGrouping extends errorRedux {
  productGroupingList: productGroupingListResponse;
  productGroupingById: productGroupingListResponse;
}

const initialState: IProductGrouping = {
  status: 'idle',
  message: '',
  productGroupingList: {
    status: '',
    message: '',
    document: [],
    rowsAffected: [],
  },
  productGroupingById: {
    status: '',
    message: '',
    document: [],
    rowsAffected: [],
  },
};

export const ProductGroupingReducer = createSlice({
  name: 'ProductGroupingStatus',
  initialState,
  reducers: {
    clearProductGroupingStatus: (state) => {
      state.status = 'idle';
      state.message = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(productGroupingList.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(productGroupingList.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
        state.productGroupingList = action.payload;
      })
      .addCase(productGroupingList.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(productGroupingById.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(productGroupingById.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
        state.productGroupingById = action.payload;
      })
      .addCase(productGroupingById.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(productGroupingCreate.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(productGroupingCreate.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
      })
      .addCase(productGroupingCreate.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(productGroupingUpdate.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(productGroupingUpdate.fulfilled, (state, action) => {
        state.status = 'completed';
        state.message = action.payload.message;
      })
      .addCase(productGroupingUpdate.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      })
      .addCase(productGroupingDelete.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(productGroupingDelete.fulfilled, (state) => {
        state.status = 'completed';
        state.message = 'Agrupamento deletado com sucesso';
      })
      .addCase(productGroupingDelete.rejected, (state, action) => {
        state.status = 'failed';
        state.message = action.payload?.message || '';
      });
  },
});

export const { clearProductGroupingStatus } = ProductGroupingReducer.actions;
export default ProductGroupingReducer.reducer;
